import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API_URL, authFetch } from '../../utils/auth';
import ToolSidebar from '../../utils/ToolSideBar';
import { features } from '../../utils/features';
import '../../../css/partner/PartnerClients.css';

const BASE_TABS = ['Overview', 'Projects', 'Sales', 'Messages'];
const APPROVED_REVIEW_STATUSES = ['approved'];
const NON_ACTIVE_REVIEW_STATUSES = ['declined', 'rejected', 'withdrawn'];
const NON_ACTIVE_REQUEST_STATUSES = ['declined', 'archived', 'withdrawn', 'rejected'];
const TERMINAL_DELIVERY_STATUSES = ['launched', 'completed', 'canceled', 'cancelled', 'rejected'];
const CLOSED_DELIVERY_STATUSES = ['canceled', 'cancelled', 'rejected'];

function hasValue(value) {
  return value !== undefined && value !== null && value !== '' && value !== 'Not provided';
}

function safeUrl(value) {
  if (!hasValue(value)) return '';
  return /^https?:\/\//i.test(value) ? value : 'https://' + value;
}

function joinNames(items) {
  return (items || []).map((item) => item.name).filter(Boolean).join(', ');
}

function isApprovedProject(project) {
  return project.status === 'accepted' || APPROVED_REVIEW_STATUSES.includes(project.review_status || '');
}

function isNonActiveRequest(project) {
  return NON_ACTIVE_REQUEST_STATUSES.includes(project.status || '') || NON_ACTIVE_REVIEW_STATUSES.includes(project.review_status || '');
}

function projectActivity(project = {}) {
  const approved = isApprovedProject(project);
  const converted = Boolean(project.converted_project);
  const terminal = TERMINAL_DELIVERY_STATUSES.includes(project.delivery_status || '');
  const closedTerminal = CLOSED_DELIVERY_STATUSES.includes(project.delivery_status || '');

  if (isNonActiveRequest(project) || (approved && converted && terminal && closedTerminal)) {
    return { key: 'closed', label: project.delivery_status_label || project.review_status_label || 'Closed', projectStatus: project.delivery_status_label || 'Closed' };
  }
  if (approved && converted && terminal) {
    return { key: 'completed', label: project.delivery_status_label || 'Completed', projectStatus: project.delivery_status_label || 'Completed' };
  }
  if (approved && converted) {
    return { key: 'active_project', label: 'Active Client', projectStatus: 'Active' };
  }
  if (approved) {
    return { key: 'approved_sales', label: 'Not Yet Active', projectStatus: 'Not Yet Active' };
  }
  return { key: 'pending_approval', label: 'Pending Approval', projectStatus: 'Not Yet Active' };
}

function normalizeProject(project = {}) {
  const activity = projectActivity(project);
  const deliveryLabel = project.delivery_status_label || 'Not started';
  const approvalStatus = isApprovedProject(project) ? 'Approved' : project.review_status_label || project.status_label || 'Pending Approval';
  const projectTypeNames = joinNames(project.project_types) || project.project_work_type_label || project.scd_project_work_type_label || '';

  return {
    ...project,
    stage: activity.key,
    stageLabel: activity.label,
    approvalStatus,
    deliveryLabel,
    projectStatus: activity.projectStatus,
    currentStage: activity.key === 'pending_approval' ? project.review_status_label || project.status_label || 'Pending Approval' : deliveryLabel,
    name: project.business_name || project.business || 'Project Request',
    type: projectTypeNames || 'Not classified',
  };
}

function statusFromProjects(projects) {
  if (projects.some((project) => project.stage === 'active_project')) return { key: 'active_project', label: 'Active Client' };
  if (projects.some((project) => project.stage === 'completed')) return { key: 'completed', label: 'Completed' };
  if (projects.some((project) => project.stage === 'approved_sales')) return { key: 'approved_sales', label: 'Not Yet Active' };
  if (projects.some((project) => project.stage === 'pending_approval')) return { key: 'pending_approval', label: 'Pending Approval' };
  return { key: 'closed', label: 'Closed' };
}

function DetailField({ label, value, href }) {
  if (!hasValue(value)) return null;
  return <div className='partner-detail-field'><span>{label}</span>{href ? <a href={href} target='_blank' rel='noopener noreferrer'>{value}</a> : <strong>{value}</strong>}</div>;
}

function EmptyState({ children }) {
  return <div className='partner-detail-empty'>{children}</div>;
}

function FeatureList({ features }) {
  if (!features?.length) return <EmptyState>No requested features are listed for this project yet.</EmptyState>;
  return (
    <div className='partner-feature-list'>
      {features.map((feature) => (
        <article className='partner-feature-card' key={feature.id || feature.name}>
          <div><h3>{feature.name}</h3><p>{feature.description || feature.category_label || 'Requested scope item'}</p></div>
          <div className='partner-feature-meta'><span>{feature.category_label}</span>{feature.base_price && <strong>${Number(feature.base_price).toLocaleString()}</strong>}{feature.requires_quote && <strong>Custom quote</strong>}</div>
        </article>
      ))}
    </div>
  );
}

function OverviewTab({ client, projects, status }) {
  const currentProject = projects[0];
  const website = safeUrl(currentProject?.current_website);
  const social = safeUrl(currentProject?.social_media);
  return (
    <div className='partner-detail-grid'>
      <section className='partner-detail-card'>
        <div className='partner-detail-card-header'>
          <h2>Client Contact</h2>
          <div className='partner-detail-actions'>
            {client.conversation_id && <Link to={'/messages/' + client.conversation_id}>Message Client</Link>}
            {features.meetingsEnabled && <Link to='/meetings/new'>Schedule Meeting</Link>}
          </div>
        </div>
        <div className='partner-detail-field-grid'>
          <DetailField label='Email' value={client.email} href={client.email ? 'mailto:' + client.email : ''} />
          <DetailField label='Phone' value={client.phone_number || currentProject?.phone} />
          <DetailField label='Preferred Contact' value={currentProject?.preferred_contact_method || 'Email'} />
          <DetailField label='Website' value={currentProject?.current_website} href={website} />
          <DetailField label='Social Media' value={currentProject?.social_media} href={social} />
        </div>
      </section>

      <section className='partner-detail-card'>
        <div className='partner-detail-card-header'><h2>Relationship / Status</h2></div>
        <div className='partner-detail-field-grid'>
          <DetailField label='Approval Status' value={currentProject?.approvalStatus || status.label} />
          <DetailField label='Project Activity' value={currentProject?.projectStatus || status.label} />
          <DetailField label='Current Lifecycle Stage' value={currentProject?.currentStage || 'No project'} />
          <DetailField label='Current Project' value={currentProject?.name || 'No project connected'} />
        </div>
      </section>

      <section className='partner-detail-card'>
        <div className='partner-detail-card-header'><h2>Business Information</h2></div>
        {!currentProject ? <EmptyState>No business information is connected yet.</EmptyState> : (
          <div className='partner-detail-field-grid'>
            <DetailField label='Business Name' value={currentProject.business_name || currentProject.business} />
            <DetailField label='Industry' value={currentProject.industry} />
            <DetailField label='Business Stage' value={currentProject.business_stage} />
            <DetailField label='Content Readiness' value={currentProject.content_status} />
            <DetailField label='Website' value={currentProject.current_website} href={website} />
            <DetailField label='Social Media' value={currentProject.social_media} href={social} />
          </div>
        )}
      </section>

      <section className='partner-detail-card'>
        <div className='partner-detail-card-header'><h2>Current Project Summary</h2></div>
        {!currentProject ? <EmptyState>No project or request is connected to this client yet.</EmptyState> : (
          <div className='partner-project-summary'>
            <div><h3>{currentProject.name}</h3><p>{currentProject.project_description || currentProject.project_goal || 'Project details are being prepared.'}</p></div>
            <div className='partner-detail-field-grid compact'>
              <DetailField label='Project Type(s)' value={currentProject.type} />
              <DetailField label='Delivery Status' value={currentProject.deliveryLabel} />
              <DetailField label='Project Activity' value={currentProject.projectStatus} />
              <DetailField label='Approval Status' value={currentProject.approvalStatus} />
              <DetailField label='Target Launch' value={currentProject.target_launch_date ? new Date(currentProject.target_launch_date).toLocaleDateString() : ''} />
              <DetailField label='Plan / Package' value={currentProject.offer?.base_plan?.name} />
              <DetailField label='Agreed Price' value={currentProject.pricing?.agreed_price ? '$' + Number(currentProject.pricing.agreed_price).toLocaleString() : ''} />
              <DetailField label='Deposit / Paid' value={currentProject.offer?.amount_paid ? '$' + Number(currentProject.offer.amount_paid).toLocaleString() + ' paid' : ''} />
            </div>
            {currentProject.stage === 'pending_approval' && <p className='partner-pending-note'>This request is waiting for internal SCD approval. No active project has been opened yet.</p>}
          </div>
        )}
      </section>

      <section className='partner-detail-card wide'>
        <div className='partner-detail-card-header'><h2>Requested Features / Scope</h2></div>
        <FeatureList features={currentProject?.features || []} />
      </section>
    </div>
  );
}

function ProjectsTab({ projects }) {
  if (!projects.length) return <EmptyState>No projects connected.</EmptyState>;
  return (
    <div className='partner-detail-list'>
      {projects.map((project) => (
        <article className='partner-project-row' key={project.id}>
          <div><h3>{project.name}</h3><p>{project.project_description || project.project_goal || project.type}</p></div>
          <div className='partner-row-meta'><span className={'partner-client-status ' + project.stage}>{project.projectStatus}</span><span>{project.deliveryLabel}</span><span>{project.approvalStatus}</span><span>{project.target_launch_date ? new Date(project.target_launch_date).toLocaleDateString() : 'No launch date'}</span></div>
          <div className='partner-project-type-summary'>{project.type}</div>
          <Link className='partner-client-button' to={'/leads/' + project.id}>Open</Link>
        </article>
      ))}
    </div>
  );
}

function moneyValue(value) {
  if (!hasValue(value)) return '';
  return '$' + Number(value).toLocaleString();
}

function offerItemMeta(item, offer) {
  if (item.is_included) {
    return {
      label: offer?.base_plan?.name ? 'Included with ' + offer.base_plan.name : 'Included',
      amount: '$0',
    };
  }
  return {
    label: item.item_type_label || 'Add-on',
    amount: moneyValue(item.effective_line_total || item.line_total),
  };
}

function releaseSummary(offer, releaseType) {
  const release = offer?.commission?.releases?.find((item) => item.release_type === releaseType);
  if (!release) return '';
  const amount = moneyValue(release.amount);
  const status = release.status_label || release.status;
  const eligible = release.payout_eligible_at ? ' - eligible ' + new Date(release.payout_eligible_at).toLocaleDateString() : '';
  return [amount, status].filter(Boolean).join(' - ') + eligible;
}

function SalesMetric({ label, value }) {
  if (!hasValue(value)) return null;
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function SalesTab({ projects }) {
  const currentProject = projects[0];
  if (!currentProject) return <EmptyState>No sales data is connected to this client yet.</EmptyState>;
  const offer = currentProject.offer;
  const payments = offer?.payments || [];
  const requestedFeatures = currentProject.features || [];
  const offerItems = offer?.items || [];

  return (
    <div className='partner-detail-list'>
      <article className='partner-sales-card'>
        <SalesMetric label='Plan / Package' value={offer?.base_plan?.name || 'No package assigned'} />
        <SalesMetric label='Project Type(s)' value={currentProject.type} />
        <SalesMetric label='Quote Status' value={offer?.status_label} />
        <SalesMetric label='Agreement Status' value={offer?.accepted_at ? 'Accepted' : offer?.status_label} />
        <SalesMetric label='Base / Agreed Price' value={currentProject.pricing?.agreed_price ? moneyValue(currentProject.pricing.agreed_price) : offer?.subtotal ? moneyValue(offer.subtotal) : ''} />
        <SalesMetric label='Quote Total' value={offer?.total_amount ? moneyValue(offer.total_amount) : ''} />
        <SalesMetric label='Deposit Amount' value={offer?.deposit_amount ? moneyValue(offer.deposit_amount) : currentProject.pricing?.deposit_amount ? moneyValue(currentProject.pricing.deposit_amount) : ''} />
        <SalesMetric label='Deposit Status' value={payments.find((payment) => payment.payment_type === 'deposit')?.status_label || (Number(offer?.amount_paid || 0) > 0 ? 'Paid' : '')} />
        <SalesMetric label='Total Commission' value={offer?.commission?.total_commission ? moneyValue(offer.commission.total_commission) : offer?.commission?.amount ? moneyValue(offer.commission.amount) : ''} />
        <SalesMetric label='Deposit Commission' value={releaseSummary(offer, 'deposit') || releaseSummary(offer, 'full')} />
        <SalesMetric label='Final Payment Commission' value={releaseSummary(offer, 'final')} />
        <SalesMetric label='Earned to Date' value={offer?.commission?.earned_to_date ? moneyValue(offer.commission.earned_to_date) : ''} />
        <SalesMetric label='Commission Rate' value={offer?.commission?.rate ? Number(offer.commission.rate).toLocaleString() + '%' : ''} />
        <SalesMetric label='Commission Basis' value={offer?.commission?.basis ? moneyValue(offer.commission.basis) : ''} />
        <SalesMetric label='Payout Schedule' value={offer?.commission?.payout_schedule_label} />
      </article>

      <section className='partner-detail-card'>
        <div className='partner-detail-card-header'><h2>Requested Features</h2></div>
        <FeatureList features={requestedFeatures} />
      </section>

      <section className='partner-detail-card'>
        <div className='partner-detail-card-header'><h2>Quote Line Items</h2></div>
        {!offerItems.length ? <EmptyState>No quote line items are available yet.</EmptyState> : (
          <div className='partner-feature-list'>
            {offerItems.map((item) => {
              const meta = offerItemMeta(item, offer);
              return (
                <article className='partner-feature-card' key={item.id || item.name}>
                  <div><h3>{item.name}</h3><p>{item.description || meta.label}</p></div>
                  <div className='partner-feature-meta'><span>{meta.label}</span><strong>{meta.amount}</strong></div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function MessagesTab({ client }) {
  if (!client.conversation_id) return <EmptyState>No partner/client conversation exists yet.</EmptyState>;
  return <Link className='partner-message-open' to={'/messages/' + client.conversation_id}>Open conversation with {client.client_name || client.name}</Link>;
}

function MeetingsTab() {
  return <div className='partner-detail-list'><EmptyState>Use the existing meeting scheduler to create or view meetings for this client.</EmptyState><Link className='partner-client-button align-start' to='/meetings/new'>Schedule Meeting</Link></div>;
}

export default function PartnerClient() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadClient() {
      try {
        const response = await authFetch(API_URL + '/partner/clients/' + clientId + '/');
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Failed to load client');
        setClient({ ...data, client_name: data.client_name || data.name || data.email || 'Client' });
      } catch (err) {
        console.error(err);
        setError(err.message || 'Failed to load client');
      } finally {
        setLoading(false);
      }
    }
    loadClient();
  }, [clientId]);

  const projects = useMemo(() => (client?.projects || []).map(normalizeProject), [client]);
  const status = useMemo(() => statusFromProjects(projects), [projects]);
  const businessName = client?.business_name || projects[0]?.name || 'Project Request';
  const headerDeliveryLabel = projects[0]?.currentStage;
  const showDeliveryBadge = headerDeliveryLabel && headerDeliveryLabel !== status.label && headerDeliveryLabel !== projects[0]?.approvalStatus;
  const tabs = features.meetingsEnabled ? [...BASE_TABS, 'Meetings'] : BASE_TABS;

  if (loading) return <div className='partner-client-shell'><ToolSidebar active='clients' /><main className='partner-client-page'><section className='partner-client-empty'><h2>Loading client...</h2></section></main></div>;

  return (
    <div className='partner-client-shell'>
      <ToolSidebar active='clients' />
      <main className='partner-client-page partner-detail-page'>
        <Link to='/clients' className='partner-back-link'>Back to clients</Link>
        {error || !client ? <section className='partner-client-empty error'><h2>Unable to load client</h2><p>{error || 'Client not found.'}</p></section> : (
          <>
            <header className='partner-detail-hero'>
              <h1>{client.client_name}</h1>
              <p>{businessName}</p>
              <div className='partner-detail-badges'><span className={'partner-client-status ' + status.key}>{status.label}</span>{showDeliveryBadge && <span className='partner-client-status neutral'>{headerDeliveryLabel}</span>}</div>
            </header>
            <nav className='partner-detail-tabs' aria-label='Client detail tabs'>{tabs.map((tab) => <button key={tab} type='button' className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>{tab}</button>)}</nav>
            <section className='partner-detail-tab-panel'>
              {activeTab === 'Overview' && <OverviewTab client={client} projects={projects} status={status} />}
              {activeTab === 'Projects' && <ProjectsTab projects={projects} />}
              {activeTab === 'Sales' && <SalesTab projects={projects} />}
              {activeTab === 'Messages' && <MessagesTab client={client} />}
              {features.meetingsEnabled && activeTab === 'Meetings' && <MeetingsTab />}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
